import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { BrowserSelection } from '../components/review/TranscriptPanel';
import type { AnchoredSelection } from '../../shared/annotations';
import type {
  AnnotationAction,
  PublicAnnotation,
  PublicComment,
  PublicReviewer,
  ReviewCreateCommentRequest,
  ReviewGetProjectResponse,
  ReviewUpdateAnnotationRequest,
} from '../../shared/types';
import ForceLight from '../components/ForceLight';
import Eyebrow from '../components/Eyebrow';
import ProgressTracker, { type ProgressTrackerStep } from '../components/ProgressTracker';
import StepFlow from '../components/StepFlow';
import Toaster, { showToast } from '../components/Toaster';
import Wordmark from '../components/Wordmark';
import AnnotationToolbar from '../components/review/AnnotationToolbar';
import CommentsPanel from '../components/review/CommentsPanel';
import OnboardingTour from '../components/review/OnboardingTour';
import {
  isTourSeen,
  markTourSeen,
  onboardingTourView,
  reviewerHasPriorActivity,
} from '../components/review/OnboardingTourGate';
import ReviewEntryPanel from '../components/review/ReviewEntryPanel';
import { FinishDialog, IdentityDialog } from '../components/review/ReviewDialogs';
import ReviewPlayer from '../components/review/ReviewPlayer';
import ReviewTransition, {
  REVIEW_TRANSITION_FADE_OUT_MS,
  reviewTransitionReleaseDelay,
  type ReviewTransitionPhase,
} from '../components/review/ReviewTransition';
import {
  dismissReviewPrompt,
  nextReviewEntryState,
  nextReviewPrompt,
  reviewedInterviewIds,
  reviewerAuthorLabel,
  reviewPrimaryGlowButton,
  reviewPromptDismissed,
  reviewQuietButton,
  reviewSecondaryButton,
  reviewTextButton,
} from '../components/review/ReviewUi';
import SelectsView from '../components/review/SelectsView';
import TranscriptPanel from '../components/review/TranscriptPanel';
import { ApiError, reviewApi } from '../lib/api';

type EntryState = 'loading' | 'pin' | 'identity' | 'error' | 'ready';
type ReviewView = 'review' | 'selects';

function friendlyError(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.status === 409) return 'That changed somewhere else. Refresh to see the latest copy, then try again.';
    return error.message;
  }
  return fallback;
}

/**
 * Identifies the line being marked, so a retry can resume it. The decision is
 * deliberately not part of the key: changing Use to Maybe on the retry is the
 * same attempt at the same line, not a second highlight over it.
 */
export function annotationSelectionKey(assetId: string, selection: AnchoredSelection): string {
  return `${assetId}:${selection.start_segment_id}:${selection.start_offset}:${selection.end_segment_id}:${selection.end_offset}`;
}

/**
 * The highlight a retry should carry on from, if there is one. A comment that
 * failed after its highlight saved resumes that highlight — but only while it
 * is still there, or a deleted one wedges every retry on an id the server has
 * already dropped.
 */
export function resumedAnnotation(
  pending: { key: string; annotationId: string } | null,
  key: string,
  annotations: readonly PublicAnnotation[],
): PublicAnnotation | null {
  if (!pending || pending.key !== key) return null;
  return annotations.find((annotation) => annotation.id === pending.annotationId) ?? null;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function activeTranscriptSegmentId(
  segments: readonly Pick<ReviewGetProjectResponse['transcript_segments'][number], 'id' | 'start_s' | 'end_s'>[],
  currentTime: number,
): string | null {
  const active = segments.find((segment) => currentTime >= segment.start_s && currentTime < segment.end_s);
  if (active) return active.id;
  const last = segments.at(-1);
  return last && currentTime >= last.end_s ? last.id : null;
}

export default function ReviewPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [entryState, setEntryState] = useState<EntryState>('loading');
  const [entryMessage, setEntryMessage] = useState<string | undefined>();
  const [entryBusy, setEntryBusy] = useState(false);
  const [data, setData] = useState<ReviewGetProjectResponse | null>(null);
  const [reviewer, setReviewer] = useState<PublicReviewer | null>(null);
  const [activeAssetId, setActiveAssetId] = useState<string | null>(null);
  const [activeSegmentId, setActiveSegmentId] = useState<string | null>(null);
  const [mediaError, setMediaError] = useState(false);
  const [browserSelection, setBrowserSelection] = useState<BrowserSelection | null>(null);
  const [annotationBusy, setAnnotationBusy] = useState(false);
  const [annotationError, setAnnotationError] = useState<string | null>(null);
  const [identityOpen, setIdentityOpen] = useState(false);
  const [identityBusy, setIdentityBusy] = useState(false);
  const [identityError, setIdentityError] = useState<string | null>(null);
  const [finishOpen, setFinishOpen] = useState(false);
  const [finishedAt, setFinishedAt] = useState<number | null>(null);
  const [commentTarget, setCommentTarget] = useState<PublicAnnotation | null>(null);
  const [promptClosed, setPromptClosed] = useState(false);
  const [tourOpen, setTourOpen] = useState(true);
  const [tourReentryOpen, setTourReentryOpen] = useState(false);
  const [transitionPhase, setTransitionPhase] = useState<ReviewTransitionPhase | null>(() =>
    prefersReducedMotion() ? null : 'in',
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const currentTimeRef = useRef(0);
  const activeSegmentIdRef = useRef<string | null>(null);
  const started = useRef(false);
  const mounted = useRef(true);
  const transitionTimers = useRef<number[]>([]);
  const pendingContribution = useRef<(() => Promise<void>) | null>(null);
  const pendingSeek = useRef<{ assetId: string; time: number } | null>(null);
  const pendingComment = useRef<{ key: string; annotationId: string } | null>(null);
  /** Whether the toolbar is still there to show a failure inline. */
  const toolbarOpen = useRef(false);

  const view: ReviewView = searchParams.get('view') === 'selects' ? 'selects' : 'review';
  const activeAsset = data?.assets.find((asset) => asset.id === activeAssetId) ?? data?.assets[0] ?? null;
  const activeVersion = data?.transcript_versions
    .filter((version) => version.asset_id === activeAsset?.id)
    .sort((left, right) => right.version - left.version)[0];
  const activeSegments = useMemo(
    () => data?.transcript_segments.filter((segment) => segment.transcript_version_id === activeVersion?.id) ?? [],
    [activeVersion?.id, data?.transcript_segments],
  );
  const activeSegmentIds = useMemo(() => new Set(activeSegments.map((segment) => segment.id)), [activeSegments]);
  const activeAnnotations = useMemo(
    () => data?.annotations.filter((annotation) =>
      annotation.asset_id === activeAsset?.id
      && activeSegmentIds.has(annotation.start_segment_id)
      && activeSegmentIds.has(annotation.end_segment_id)) ?? [],
    [activeAsset?.id, activeSegmentIds, data?.annotations],
  );
  const activeComments = useMemo(
    () => data?.comments.filter((comment) => !comment.asset_id || comment.asset_id === activeAsset?.id) ?? [],
    [activeAsset?.id, data?.comments],
  );
  const handlePlaybackTime = useCallback((seconds: number) => {
    currentTimeRef.current = seconds;
    const nextSegmentId = activeTranscriptSegmentId(activeSegments, seconds);
    if (activeSegmentIdRef.current === nextSegmentId) return;
    activeSegmentIdRef.current = nextSegmentId;
    setActiveSegmentId(nextSegmentId);
  }, [activeSegments]);
  const getCurrentTime = useCallback(() => currentTimeRef.current, []);

  useEffect(() => {
    const nextSegmentId = activeTranscriptSegmentId(activeSegments, currentTimeRef.current);
    if (activeSegmentIdRef.current === nextSegmentId) return;
    activeSegmentIdRef.current = nextSegmentId;
    setActiveSegmentId(nextSegmentId);
  }, [activeSegments]);
  const reviewedIds = useMemo(
    () => reviewedInterviewIds(data?.annotations ?? [], reviewer?.id ?? null),
    [data?.annotations, reviewer?.id],
  );
  const reviewedCount = data?.assets.filter((asset) => reviewedIds.has(asset.id)).length ?? 0;
  const reviewPrompt = nextReviewPrompt(data?.assets ?? [], activeAsset?.id ?? null, reviewedIds);
  const projectId = data?.project.id ?? null;
  // Dismissing the nudge is remembered once per project, not per interview.
  const promptDismissed = useMemo(
    () => promptClosed || (projectId !== null && reviewPromptDismissed(projectId)),
    [projectId, promptClosed],
  );
  const selectsCount = reviewer
    ? data?.annotations.filter((annotation) => annotation.reviewer_id === reviewer.id && annotation.action === 'use').length ?? 0
    : 0;
  const tourView = data && reviewer
    ? onboardingTourView({
      seen: isTourSeen(),
      priorActivity: reviewerHasPriorActivity(
        reviewer.id,
        data.annotations,
        data.comments,
        reviewer.finished_at,
      ),
      isOwner: reviewer.is_owner === 1,
      tourOpen,
      reentryOpen: tourReentryOpen,
      allowTourSkip: data.project.allowTourSkip,
    })
    : { mode: null, allowSkip: false, writesSeenOnComplete: false };
  const blockReviewInteraction = tourView.mode === 'gated';

  const clearTransitionTimers = useCallback(() => {
    for (const timer of transitionTimers.current) window.clearTimeout(timer);
    transitionTimers.current = [];
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTransitionTimers();
    };
  }, [clearTransitionTimers]);

  useEffect(() => {
    toolbarOpen.current = browserSelection !== null;
  }, [browserSelection]);

  const beginTransition = useCallback((): number | null => {
    clearTransitionTimers();
    if (prefersReducedMotion()) {
      setTransitionPhase(null);
      return null;
    }
    setTransitionPhase('in');
    return Date.now();
  }, [clearTransitionTimers]);

  const releaseTransition = useCallback((startedAt: number | null) => {
    if (startedAt === null || !mounted.current) return;
    const releaseTimer = window.setTimeout(() => {
      setTransitionPhase('out');
      const unmountTimer = window.setTimeout(
        () => setTransitionPhase(null),
        REVIEW_TRANSITION_FADE_OUT_MS,
      );
      transitionTimers.current.push(unmountTimer);
    }, reviewTransitionReleaseDelay(startedAt));
    transitionTimers.current.push(releaseTimer);
  }, []);

  const openSession = useCallback(async (pin?: string) => {
    const transitionStartedAt = beginTransition();
    setEntryBusy(true);
    setEntryMessage(undefined);
    let sessionOpened = false;
    try {
      const session = await reviewApi.startSession({ token, ...(pin ? { pin } : {}) });
      sessionOpened = true;
      const project = await reviewApi.getProject();
      setData(project);
      setReviewer(session.reviewer);
      setFinishedAt(session.reviewer?.finished_at ?? null);
      const requestedAsset = searchParams.get('asset');
      setActiveAssetId(project.assets.some((asset) => asset.id === requestedAsset) ? requestedAsset : project.assets[0]?.id ?? null);
      setEntryState(nextReviewEntryState(session.reviewer));
    } catch (error) {
      const message = friendlyError(error, 'We could not open this review. Mind trying again?');
      if (error instanceof ApiError && error.status === 401 && !sessionOpened) {
        setEntryState('pin');
        setEntryMessage(message.includes('Enter the PIN') ? undefined : message);
      } else {
        setEntryState('error');
        setEntryMessage(message);
      }
    } finally {
      setEntryBusy(false);
      releaseTransition(transitionStartedAt);
    }
  }, [beginTransition, releaseTransition, searchParams, token]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void openSession();
  }, [openSession]);

  useEffect(() => {
    const pending = pendingSeek.current;
    const video = videoRef.current;
    if (!pending || !video || pending.assetId !== activeAsset?.id) return;
    const seek = () => {
      video.currentTime = pending.time;
      handlePlaybackTime(pending.time);
      pendingSeek.current = null;
    };
    if (video.readyState >= 1) seek();
    else video.addEventListener('loadedmetadata', seek, { once: true });
    return () => video.removeEventListener('loadedmetadata', seek);
  }, [activeAsset?.id, handlePlaybackTime]);

  function setView(next: ReviewView) {
    const params = new URLSearchParams(searchParams);
    if (next === 'selects') params.set('view', 'selects'); else params.delete('view');
    setSearchParams(params, { replace: true });
  }

  function chooseAsset(assetId: string) {
    setActiveAssetId(assetId);
    currentTimeRef.current = 0;
    if (activeSegmentIdRef.current !== null) {
      activeSegmentIdRef.current = null;
      setActiveSegmentId(null);
    }
    setMediaError(false);
    const params = new URLSearchParams(searchParams);
    // Opening an interview always lands on the interviews view, whichever view asked for it.
    params.delete('view');
    params.set('asset', assetId);
    setSearchParams(params, { replace: true });
  }

  function closePrompt() {
    if (projectId) dismissReviewPrompt(projectId);
    setPromptClosed(true);
  }

  function completeTour() {
    if (tourView.writesSeenOnComplete) markTourSeen();
    if (tourView.mode === 'gated') setTourOpen(false);
    if (tourView.mode === 'reentry') setTourReentryOpen(false);
  }

  function closeTourReentry() {
    setTourReentryOpen(false);
  }

  function seek(seconds: number) {
    const video = videoRef.current;
    if (video) video.currentTime = seconds;
    handlePlaybackTime(seconds);
  }

  function seekToAsset(assetId: string, seconds: number, segmentId?: string) {
    setView('review');
    pendingSeek.current = { assetId, time: seconds };
    if (assetId === activeAsset?.id) {
      seek(seconds);
      pendingSeek.current = null;
    } else {
      chooseAsset(assetId);
    }
    if (segmentId) window.setTimeout(() => document.querySelector(`[data-segment-id="${CSS.escape(segmentId)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 120);
  }

  async function withIdentity(task: () => Promise<void>): Promise<boolean> {
    if (reviewer) {
      await task();
      return true;
    }
    pendingContribution.current = task;
    setIdentityError(null);
    setIdentityOpen(true);
    return false;
  }

  async function identify(name: string, email: string | null) {
    setIdentityBusy(true);
    setIdentityError(null);
    try {
      const response = await reviewApi.identify({ display_name: name, email });
      setReviewer(response.reviewer);
      setData((current) => current ? { ...current, reviewers: [...current.reviewers.filter((item) => item.id !== response.reviewer.id), response.reviewer] } : current);
      if (entryState === 'identity') setEntryState('ready');
      setIdentityOpen(false);
      showToast('success', 'Your name is saved.');
      const pending = pendingContribution.current;
      pendingContribution.current = null;
      if (pending) {
        try {
          await pending();
        } catch (error) {
          // The dialog has closed and the toolbar may have gone with it, so
          // without a toast this failure would disappear entirely.
          if (!toolbarOpen.current) {
            showToast('error', error instanceof Error && error.message
              ? error.message
              : 'We saved your name, but not that change. Mind trying again?');
          }
        }
      }
    } catch (error) {
      setIdentityError(friendlyError(error, 'We could not save your name. Mind trying again?'));
    } finally {
      setIdentityBusy(false);
    }
  }

  /**
   * One path for marking a line: the highlight is stored as an annotation and
   * any comment typed alongside it becomes a real comment anchored to that
   * annotation, so it lands in the Comments panel like every other comment.
   * The annotation carries no note of its own — the comment row owns the text.
   */
  async function createAnnotation(action: AnnotationAction, comment: string | null) {
    if (!browserSelection || !activeAsset) return;
    const captured = browserSelection;
    const assetId = activeAsset.id;
    const key = annotationSelectionKey(assetId, captured.selection);
    const resumed = resumedAnnotation(pendingComment.current, key, data?.annotations ?? []);
    // Nothing left to resume, so the next failure is a fresh one to report.
    if (!resumed) pendingComment.current = null;
    const save = async () => {
      setAnnotationBusy(true);
      setAnnotationError(null);
      try {
        let annotationId = resumed?.id ?? null;
        if (resumed) {
          // The retry asked for a different decision: move the highlight that
          // already saved rather than marking the same line a second time.
          if (resumed.action !== action) {
            const response = await reviewApi.updateAnnotation(resumed.id, { action });
            setData((current) => current ? { ...current, annotations: current.annotations.map((item) => item.id === resumed.id ? response.annotation : item) } : current);
          }
        } else {
          const response = await reviewApi.createAnnotation({ asset_id: assetId, action, ...captured.selection, note: null });
          setData((current) => current ? { ...current, annotations: [...current.annotations, response.annotation] } : current);
          annotationId = response.annotation.id;
        }
        if (comment && annotationId) {
          pendingComment.current = { key, annotationId };
          const response = await reviewApi.createComment({ annotation_id: annotationId, asset_id: assetId, body: comment });
          setData((current) => current ? { ...current, comments: [...current.comments, response.comment] } : current);
        }
        pendingComment.current = null;
        setBrowserSelection(null);
        window.getSelection()?.removeAllRanges();
        showToast('success', comment ? 'Comment saved.' : 'Annotation saved.');
      } catch (error) {
        const message = friendlyError(error, pendingComment.current?.key === key
          ? 'We saved the highlight but not the comment. Mind trying again?'
          : 'We could not save that annotation. Mind trying again?');
        setAnnotationError(message);
        // Worded on the way out, so a caller that outlives the toolbar can
        // still tell the reviewer what went wrong.
        throw new Error(message);
      } finally {
        setAnnotationBusy(false);
      }
    };
    await withIdentity(save);
  }

  async function editAnnotation(annotation: PublicAnnotation, action: AnnotationAction, note: string | null) {
    const body: ReviewUpdateAnnotationRequest = { action, note };
    try {
      const response = await reviewApi.updateAnnotation(annotation.id, body);
      setData((current) => current ? { ...current, annotations: current.annotations.map((item) => item.id === annotation.id ? response.annotation : item) } : current);
      showToast('success', 'Annotation updated.');
    } catch (error) {
      throw new Error(friendlyError(error, 'We could not update that annotation. Mind trying again?'));
    }
  }

  async function deleteAnnotation(annotation: PublicAnnotation) {
    try {
      await reviewApi.deleteAnnotation(annotation.id);
      // The server deletes the comments anchored to it in the same batch, so
      // dropping them here keeps the page and the next load in step.
      setData((current) => current ? { ...current, annotations: current.annotations.filter((item) => item.id !== annotation.id), comments: current.comments.filter((comment) => comment.annotation_id !== annotation.id) } : current);
      if (pendingComment.current?.annotationId === annotation.id) pendingComment.current = null;
      showToast('success', 'Annotation deleted.');
    } catch (error) {
      throw new Error(friendlyError(error, 'We could not delete that annotation. Mind trying again?'));
    }
  }

  async function addComment(request: ReviewCreateCommentRequest) {
    const save = async () => {
      try {
        const response = await reviewApi.createComment(request);
        setData((current) => current ? { ...current, comments: [...current.comments, response.comment] } : current);
        showToast('success', 'Comment saved.');
      } catch (error) {
        throw new Error(friendlyError(error, 'We could not save that comment. Mind trying again?'));
      }
    };
    await withIdentity(save);
  }

  async function deleteComment(comment: PublicComment) {
    try {
      await reviewApi.deleteComment(comment.id);
      setData((current) => current ? { ...current, comments: current.comments.filter((item) => item.id !== comment.id) } : current);
      showToast('success', 'Comment deleted.');
    } catch (error) {
      throw new Error(friendlyError(error, 'We could not delete that comment. Mind trying again?'));
    }
  }

  function openFinish() {
    void withIdentity(async () => setFinishOpen(true));
  }

  function updateReviewer(nextReviewer: PublicReviewer) {
    setReviewer(nextReviewer);
    setData((current) => current
      ? {
        ...current,
        reviewers: [
          ...current.reviewers.filter((item) => item.id !== nextReviewer.id),
          nextReviewer,
        ],
      }
      : current);
  }

  async function finish() {
    try {
      const response = await reviewApi.finish();
      setFinishedAt(response.finished_at);
      showToast('success', 'Review submitted.');
    } catch (error) {
      throw new Error(friendlyError(error, 'We could not submit your review. Mind trying again?'));
    }
  }

  // One ForceLight and one transition overlay wrap both branches: swapping the
  // entry screen for the review page must not tear down the theme pin or the
  // green overlay, or an ink-coloured frame paints through the gap.
  let body: ReactNode;

  if (entryState !== 'ready' || !data) {
    body = (
      <ReviewEntryPanel
        state={entryState === 'ready' ? 'loading' : entryState}
        message={entryState === 'identity' ? identityError ?? undefined : entryMessage}
        busy={entryState === 'identity' ? identityBusy : entryBusy}
        onPin={openSession}
        onIdentify={identify}
        onRetry={() => openSession()}
      />
    );
  } else {
    const lastCueEnd = activeSegments.at(-1)?.end_s;
    const durationMismatch = Boolean(activeAsset?.duration_s && lastCueEnd && Math.abs(activeAsset.duration_s - lastCueEnd) > Math.max(2, activeAsset.duration_s * 0.05));
    const allInterviewsReviewed = data.assets.length > 0 && reviewedCount === data.assets.length;
    const progressSteps: ProgressTrackerStep[] = [
      ...data.assets.map((asset, index): ProgressTrackerStep => ({
        label: `Interview ${index + 1}`,
        state: reviewedIds.has(asset.id)
          ? 'done'
          : view === 'review' && asset.id === activeAsset?.id
            ? 'active'
            : 'pending',
        onSelect: () => chooseAsset(asset.id),
      })),
      {
        label: 'Selects',
        state: finishedAt ? 'done' : view === 'selects' || allInterviewsReviewed ? 'active' : 'pending',
        onSelect: () => setView('selects'),
      },
      {
        label: 'Submit',
        state: finishedAt ? 'done' : view === 'selects' && allInterviewsReviewed ? 'active' : 'pending',
      },
    ];
    const activeInterviewNumber = Math.max(1, data.assets.findIndex((asset) => asset.id === activeAsset?.id) + 1);
    const promptInterviewNumber = reviewPrompt?.type === 'interview'
      ? data.assets.findIndex((asset) => asset.id === reviewPrompt.assetId) + 1
      : null;

    // The annotation toolbar is placed absolutely in document space, so this
    // wrapper is its positioning context.
    body = (
      <div
        className={`relative min-h-screen bg-page text-fg${blockReviewInteraction ? ' pointer-events-none' : ''}`}
        aria-hidden={blockReviewInteraction ? true : undefined}
      >
        <a href="#review-main" className="app-button fixed left-3 top-3 z-[60] inline-flex min-h-11 -translate-y-24 items-center rounded-full border border-transparent bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover focus:translate-y-0">Skip to review</a>
        <header className="sticky top-0 z-40 border-b border-line bg-page/80 px-5 py-3 backdrop-blur md:px-10">
          <div className="mx-auto grid max-w-[1500px] items-center gap-3 md:grid-cols-[1fr_auto_1fr] md:gap-4">
            <div className="flex items-baseline gap-3">
              <Wordmark />
              <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-faint">· Client review</span>
            </div>
            <nav className="order-last flex justify-center md:order-none" aria-label="Views">
              <span className="tabs tabs-switch">
                <button type="button" className={`tab ${view === 'review' ? 'tab-active' : ''}`} onClick={() => setView('review')} aria-current={view === 'review' ? 'page' : undefined}>Interviews</button>
                <button type="button" className={`tab ${view === 'selects' ? 'tab-active' : ''}`} onClick={() => setView('selects')} aria-current={view === 'selects' ? 'page' : undefined}>Selects{selectsCount > 0 ? <span className="tab-count">{selectsCount}</span> : null}</button>
              </span>
            </nav>
            <div className="flex flex-wrap items-center gap-3 md:justify-end">
              {reviewer
                ? <span className="text-sm text-muted">Reviewing as <b className="font-semibold text-fg">{reviewerAuthorLabel(reviewer)}</b> · <button type="button" className={reviewTextButton} onClick={() => setIdentityOpen(true)}>Change</button></span>
                : <button type="button" className={reviewSecondaryButton} onClick={() => setIdentityOpen(true)}>Add your name</button>}
              <button type="button" className={finishedAt ? reviewQuietButton : reviewSecondaryButton} onClick={openFinish}>{finishedAt ? 'Review submitted' : 'Submit review'}</button>
            </div>
          </div>
        </header>
        <main id="review-main">
          {view === 'selects' ? <SelectsView assets={data.assets} segments={data.transcript_segments} annotations={data.annotations} reviewers={data.reviewers} reviewer={reviewer} canEditAll={data.can_edit_all} onOpen={seekToAsset} onEdit={editAnnotation} /> : (
            <>
              <section className="border-b border-line py-12 md:py-14">
                <div className="mx-auto flex max-w-[1500px] flex-wrap items-end justify-between gap-10 px-5 md:px-10">
                  <div className="max-w-4xl">
                    <Eyebrow accent className="rise">Client review</Eyebrow>
                    <Eyebrow className="rise mt-2 block" style={{ animationDelay: '60ms' }}>Prepared for {data.project.client_name}</Eyebrow>
                    <h1 className="display-hero rise mt-5" style={{ animationDelay: '120ms' }}>{data.project.title}</h1>
                    {data.project.instructions ? <p className="rise mt-6 max-w-3xl whitespace-pre-wrap text-[15px] text-muted" style={{ animationDelay: '200ms' }}>{data.project.instructions}</p> : <p className="rise mt-6 text-[15px] text-muted" style={{ animationDelay: '200ms' }}>Watch, read, and mark the lines that stand out.</p>}
                    {reviewer && tourView.mode !== 'gated' ? (
                      <button type="button" className={`${reviewTextButton} rise mt-3 text-sm`} style={{ animationDelay: '240ms' }} onClick={() => setTourReentryOpen(true)}>See the tour again</button>
                    ) : null}
                  </div>
                  <div className="rise -mx-1.5 w-full max-w-2xl overflow-x-auto px-1.5 pb-2 pt-1.5 lg:w-auto" style={{ animationDelay: '280ms' }}>
                    <ProgressTracker steps={progressSteps} ariaLabel="Review progress" className="min-w-[32rem]" />
                  </div>
                </div>
              </section>
              <div className="mx-auto max-w-[1500px] px-5 py-8 md:px-10">
              {data.assets.length > 1 ? (
                <nav className="mt-6" aria-label="Interviews">
                  <div className="flex flex-wrap items-baseline justify-between gap-3">
                    <span className="mono-label">Interviews</span>
                    <span className="font-mono text-[11px] text-faint">{reviewedCount} of {data.assets.length} reviewed</span>
                  </div>
                  {/* The scroll container clips vertically, so keep clearance for the focus ring. */}
                  <div className="-mx-1.5 mt-3 flex gap-2 overflow-x-auto px-1.5 pb-2 pt-1.5">
                    {data.assets.map((asset) => {
                      const reviewed = reviewedIds.has(asset.id);
                      return <button key={asset.id} type="button" className={`int-pill ${asset.id === activeAsset?.id ? 'int-pill-active' : ''}`.trim()} onClick={() => chooseAsset(asset.id)} aria-current={asset.id === activeAsset?.id ? 'true' : undefined} aria-label={`${asset.title}${reviewed ? ', reviewed' : ''}`}>{reviewed ? <span className="int-tick" aria-hidden="true" /> : null}{asset.title}</button>;
                    })}
                  </div>
                </nav>
              ) : null}
              {durationMismatch ? <div className="mt-5 rounded-input border border-warning/20 bg-warning-tint px-4 py-3 text-sm text-warning" role="status">The transcript timing and video length differ a little. Click-to-seek still uses the transcript cue time.</div> : null}
              {reviewPrompt && !promptDismissed ? (
                <div className="card-accent rise-subtle mt-6 flex flex-wrap items-center justify-between gap-8 px-6 py-5 md:px-8" style={{ animationDelay: '340ms' }} role="status">
                  <StepFlow
                    className="min-w-0 flex-1 md:min-w-[30rem]"
                    steps={[
                      {
                        title: 'Watch and mark',
                        detail: reviewedIds.has(activeAsset?.id ?? '') ? `Interview ${activeInterviewNumber} is done. Nice one.` : `Review interview ${activeInterviewNumber} and mark the lines that stand out.`,
                        state: reviewedIds.has(activeAsset?.id ?? '') ? 'done' : 'current',
                      },
                      {
                        title: reviewPrompt.type === 'interview' ? `Next: Interview ${promptInterviewNumber}` : 'Review your Selects',
                        detail: reviewPrompt.type === 'interview' ? reviewPrompt.title : 'Check your picks across every interview.',
                        state: 'current',
                      },
                      {
                        title: 'Submit your review',
                        detail: 'Check your selects, then send them to Henry.',
                        state: 'pending',
                      },
                    ]}
                  />
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className={reviewPrimaryGlowButton} onClick={() => reviewPrompt.type === 'interview' ? chooseAsset(reviewPrompt.assetId) : setView('selects')}>{reviewPrompt.type === 'interview' ? `Open interview ${promptInterviewNumber}` : 'Review selects'}</button>
                    <button type="button" className={reviewQuietButton} onClick={closePrompt}>Got it</button>
                  </div>
                </div>
              ) : null}
              <div className="mt-7 grid gap-7 lg:grid-cols-[minmax(0,1fr)_minmax(420px,36vw)] lg:items-start xl:gap-10">
                <div className="order-2 min-w-0 lg:order-1">
                  <TranscriptPanel interviewNumber={activeInterviewNumber} interviewTitle={activeAsset?.title} speakerLabel={activeAsset?.speaker_label} duration={activeAsset?.duration_s} segments={activeSegments} annotations={activeAnnotations} comments={activeComments} reviewers={data.reviewers} reviewer={reviewer} activeSegmentId={activeSegmentId} onSeek={seek} onSelect={(selection) => { setAnnotationError(null); setBrowserSelection(selection); }} onEdit={editAnnotation} onDelete={deleteAnnotation} onComment={(annotation) => { setCommentTarget(annotation); document.getElementById('review-comments')?.scrollIntoView({ behavior: 'smooth' }); }} />
                </div>
                <aside className="contents lg:sticky lg:top-20 lg:order-2 lg:block lg:space-y-5" aria-label="Video and comments">
                  <div className="rise-subtle sticky top-16 z-30 order-1 bg-page pb-2 lg:static lg:z-auto lg:bg-transparent lg:pb-0" style={{ animationDelay: '500ms' }}><ReviewPlayer asset={activeAsset} videoRef={videoRef} mediaError={mediaError} onTime={handlePlaybackTime} onMediaError={() => setMediaError(true)} /></div>
                  <div id="review-comments" className="rise-subtle order-3" style={{ animationDelay: '560ms' }}><CommentsPanel comments={data.comments} annotations={data.annotations} reviewers={data.reviewers} reviewer={reviewer} assets={data.assets} activeAsset={activeAsset} getCurrentTime={getCurrentTime} targetAnnotation={commentTarget} onClearTarget={() => setCommentTarget(null)} onAdd={addComment} onDelete={deleteComment} onSeek={(assetId, time) => seekToAsset(assetId, time)} /></div>
                </aside>
              </div>
            </div>
            </>
          )}
        </main>
        <footer className="site-footer mt-20 px-5 md:px-10">
          <div className="footer-row mx-auto max-w-[1500px]">
            <span>Sheahan Studios · Confidential client review</span>
            <span>Review / {data.project.id}</span>
          </div>
        </footer>
        {browserSelection ? <AnnotationToolbar selection={browserSelection.selection} quote={browserSelection.quote} anchor={browserSelection.anchor} source={browserSelection.source} busy={annotationBusy} error={annotationError} onSubmit={createAnnotation} onClose={() => { setBrowserSelection(null); setAnnotationError(null); pendingComment.current = null; window.getSelection()?.removeAllRanges(); }} /> : null}
        {identityOpen ? <IdentityDialog busy={identityBusy} error={identityError} onClose={() => { setIdentityOpen(false); pendingContribution.current = null; }} onIdentify={identify} /> : null}
        {finishOpen && reviewer ? (
          <FinishDialog
            projectTitle={data.project.title}
            reviewer={reviewer}
            onClose={() => setFinishOpen(false)}
            onReviewerChange={updateReviewer}
            onFinish={finish}
          />
        ) : null}
      </div>
    );
  }

  return (
    <ForceLight>
      {body}
      <Toaster />
      {entryState === 'ready'
        && transitionPhase === null
        && data !== null
        && reviewer !== null
        && tourView.mode !== null
        ? (
          <OnboardingTour
            dismissible={tourView.mode === 'reentry'}
            allowSkip={tourView.allowSkip}
            onComplete={completeTour}
            onSkip={completeTour}
            onDismiss={tourView.mode === 'reentry' ? closeTourReentry : undefined}
          />
        )
        : null}
      {transitionPhase ? <ReviewTransition phase={transitionPhase} /> : null}
    </ForceLight>
  );
}
